import { useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type {
  TodoItemStatus,
  TodoPersonalItemResponse,
  TodoPersonalSectionResponse,
  TodoPlacedTaskResponse,
  TodoPersonalEntryRef,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';
import { useTodoSections } from './useTodoSections';

/** A single card on the board — either a private item or an assigned shared task. */
export interface BoardEntry {
  kind: 'personal' | 'shared';
  /** personalItemId (personal) or itemId (shared). */
  id: string;
  sortOrder: number;
  title: string;
  status: TodoItemStatus;
  personal?: TodoPersonalItemResponse;
  shared?: TodoPlacedTaskResponse;
  /** Checklist progress ("N/M") — drives the card badge (server-computed). */
  checklistTotal: number;
  checklistDone: number;
  /** The workspace project this task belongs to; null when unassigned. */
  projectId: string | null;
  /**
   * Set on a shared task shown OUTSIDE its shared-section "home" — either moved
   * by the user into another section, or landed in the inbox because no personal
   * section matches its shared section by name. Carries the shared section name
   * for the "belongs to X on the shared list" badge.
   */
  displacedFromSection?: string;
}

export interface BoardColumn {
  /** null = the default "unsectioned" column. */
  personalSectionId: string | null;
  name: string;
  /** Parent section when nested; null for a top-level or the unsectioned column. */
  parentPersonalSectionId: string | null;
  entries: BoardEntry[];
}

const UNSECTIONED = '__none__';

/** How to reconcile the personal board with the shared list's sections. */
export type TodoPersonalBoardSyncMode = 'add_missing' | 'items_and_structure' | 'reset';

/**
 * The personal "My Tasks" board for a list: your private sections + items merged
 * with the shared tasks assigned to you, arranged in your own order. Every
 * mutation invalidates the board so it re-derives.
 */
export function useMyTasksBoard(holderId: string | null, listId: string, enabled = true) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Surface any board mutation failure — without this, fire-and-forget single
  // actions (verify / done / reassign / status / delete …) fail silently. The two
  // mutations whose callers own their own error UX (syncBoard, createSubtaskOnBoard)
  // deliberately opt out below to avoid a double toast.
  //
  // Suppressed while a bulk action is in flight (see withBulkErrorSuppressed): the
  // bulk runner aggregates failures into one summary toast, so per-request toasts
  // would stack (three failures → three generic toasts + the summary). A DEPTH
  // counter, not a boolean, so overlapping bulk actions don't unsuppress early —
  // the first to finish must not clear suppression while a second is still running.
  const errorSuppressionDepthRef = useRef(0);
  const onError = () => {
    if (errorSuppressionDepthRef.current > 0) return;
    toast({ title: 'Something went wrong', variant: 'destructive' });
  };
  const key = queryKeys.todoLists.personalBoard(holderId ?? '', listId);

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api.getTodoPersonalBoard(holderId as string, listId),
    enabled: enabled && !!holderId && !!listId,
  });
  // Shared-list sections drive the name-based default placement + the seed.
  const { sections: sharedSections } = useTodoSections(holderId, listId);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });
  const h = holderId as string;
  // For mutations that touch a SHARED task (not a private overlay row), the List
  // view, Completed timeline, sub-task index, and cross-workspace count badges
  // must refresh too — not just this board. `detail(h, listId)` is a prefix that
  // covers the board (`personalBoard`) plus items/completed/subtaskIndex.
  const invalidateShared = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.detail(h, listId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.todoLists.my(h) });
  };

  const columns = useMemo<BoardColumn[]>(() => {
    const sections = data?.sections ?? [];
    const personalItems = data?.personalItems ?? [];
    const assignedTasks = data?.assignedTasks ?? [];

    const cols: BoardColumn[] = [
      { personalSectionId: null, name: 'Unsectioned', parentPersonalSectionId: null, entries: [] },
    ];
    for (const s of sections) {
      cols.push({
        personalSectionId: s.personalSectionId,
        name: s.name,
        parentPersonalSectionId: s.parentPersonalSectionId,
        entries: [],
      });
    }
    const byKey = new Map(cols.map((c) => [c.personalSectionId ?? UNSECTIONED, c]));
    const target = (secId: string | null) =>
      byKey.get(secId ?? UNSECTIONED) ?? byKey.get(UNSECTIONED)!;

    // Name-based mapping: a shared task with NO explicit placement defaults into
    // the personal section whose NAME matches its shared section (the seed copies
    // names, so they line up). Matching by name — not a stored link — is what
    // makes rename/delete gracefully fall back to the inbox until re-synced.
    const sharedSectionNameById = new Map(sharedSections.map((s) => [s.sectionId, s.name]));
    const personalSectionNameById = new Map(sections.map((s) => [s.personalSectionId, s.name]));
    const personalSectionIdByName = new Map<string, string>();
    for (const s of sections) {
      if (!personalSectionIdByName.has(s.name))
        personalSectionIdByName.set(s.name, s.personalSectionId);
    }

    for (const pi of personalItems) {
      target(pi.personalSectionId).entries.push({
        kind: 'personal',
        id: pi.personalItemId,
        sortOrder: pi.sortOrder,
        title: pi.title,
        status: pi.status,
        checklistTotal: pi.checklistTotal ?? 0,
        checklistDone: pi.checklistDone ?? 0,
        projectId: pi.projectId ?? null,
        personal: pi,
      });
    }
    for (const t of assignedTasks) {
      const sharedSectionName = t.sectionId ? sharedSectionNameById.get(t.sectionId) : undefined;
      const homeSectionId = sharedSectionName
        ? personalSectionIdByName.get(sharedSectionName)
        : undefined;
      // Explicit placement wins; otherwise fall to the name-matched home (or the
      // inbox when nothing matches).
      const columnSectionId =
        t.placement !== null ? t.placement.personalSectionId : (homeSectionId ?? null);
      const columnName = columnSectionId ? personalSectionNameById.get(columnSectionId) : undefined;
      // Displaced = it has a shared section but isn't sitting in a section that
      // matches it → show the "belongs to X" badge.
      const displaced = !!sharedSectionName && columnName !== sharedSectionName;
      target(columnSectionId).entries.push({
        kind: 'shared',
        id: t.itemId,
        // Placed tasks use their arranged order; unplaced ones follow the shared
        // list's order within their home section.
        sortOrder: t.placement !== null ? t.placement.sortOrder : t.sortOrder,
        title: t.title,
        status: t.status,
        checklistTotal: t.checklistTotal ?? 0,
        checklistDone: t.checklistDone ?? 0,
        projectId: t.projectId ?? null,
        shared: t,
        displacedFromSection: displaced ? sharedSectionName : undefined,
      });
    }
    for (const c of cols) {
      c.entries.sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
    }
    return cols;
  }, [data, sharedSections]);

  const mCreateSection = useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      parentPersonalSectionId?: string | null;
    }) => api.createTodoPersonalSection(h, listId, input),
    onSuccess: invalidate,
    onError,
  });
  const mUpdateSection = useMutation({
    mutationFn: (v: {
      id: string;
      input: {
        name?: string;
        description?: string | null;
        parentPersonalSectionId?: string | null;
      };
    }) => api.updateTodoPersonalSection(h, v.id, v.input),
    onSuccess: invalidate,
    onError,
  });
  const mDeleteSection = useMutation({
    mutationFn: (id: string) => api.deleteTodoPersonalSection(h, id),
    onSuccess: invalidate,
    onError,
  });
  const mReorderSections = useMutation({
    mutationFn: (order: string[]) => api.reorderTodoPersonalSections(h, listId, order),
    onSuccess: invalidate,
    onError,
  });
  const mCreateItem = useMutation({
    mutationFn: (v: { personalSectionId: string | null; title: string; description?: string }) =>
      api.createTodoPersonalItem(h, listId, v),
    onSuccess: invalidate,
    onError,
  });
  const mUpdateItem = useMutation({
    mutationFn: (v: {
      id: string;
      title?: string;
      description?: string | null;
      status?: TodoItemStatus;
      personalSectionId?: string | null;
      projectId?: string | null;
    }) =>
      api.updateTodoPersonalItem(h, v.id, {
        title: v.title,
        description: v.description,
        status: v.status,
        personalSectionId: v.personalSectionId,
        projectId: v.projectId,
      }),
    onSuccess: invalidate,
    onError,
  });
  const mDeleteItem = useMutation({
    mutationFn: (id: string) => api.deleteTodoPersonalItem(h, id),
    onSuccess: invalidate,
    onError,
  });
  const mRestoreItem = useMutation({
    mutationFn: (id: string) => api.restoreTodoPersonalItem(h, id),
    onSuccess: invalidate,
    onError,
  });
  const mSetSharedStatus = useMutation({
    mutationFn: (v: { itemId: string; status: TodoItemStatus }) =>
      api.updateTodoItem(h, v.itemId, { status: v.status }),
    onSuccess: invalidateShared,
    onError,
  });
  // Toggle a shared task's `done` flag (the review-list checkbox) — orthogonal to
  // status; the server couples completed⇒done and done⇒in_progress.
  const mSetSharedDone = useMutation({
    mutationFn: (v: { itemId: string; done: boolean }) =>
      api.updateTodoItem(h, v.itemId, { done: v.done }),
    onSuccess: invalidateShared,
    onError,
  });
  const mUpdateSharedItem = useMutation({
    mutationFn: (v: {
      itemId: string;
      input: {
        title?: string;
        description?: string | null;
        sectionId?: string | null;
        projectId?: string | null;
      };
    }) => api.updateTodoItem(h, v.itemId, v.input),
    onSuccess: invalidateShared,
    onError,
  });
  // Send an assigned shared task for review: mirrors the List view's
  // onRequestReview — sets status='in_review' and routes the request to one
  // reviewer (null = broadcast to all list reviewers). Server-allowed for the
  // assignee (the board only ever shows tasks assigned to the caller).
  const mRequestReview = useMutation({
    mutationFn: (v: { itemId: string; reviewerPrincipalId: string | null }) =>
      api.updateTodoItem(h, v.itemId, {
        status: 'in_review',
        reviewAssignedToPrincipalId: v.reviewerPrincipalId,
      }),
    onSuccess: invalidateShared,
    onError,
  });
  // Reassign a shared task from this board to someone else (or clear it). The
  // board only ever shows tasks assigned to the caller, so the server allows the
  // assignee to hand off their own work (assign_item / unassign_item). A task
  // reassigned away leaves this board, so refresh the wider shared tree too.
  const mAssignShared = useMutation({
    mutationFn: (v: { itemId: string; assigneePrincipalId: string }) =>
      api.assignTodoItem(h, v.itemId, v.assigneePrincipalId),
    onSuccess: invalidateShared,
    onError,
  });
  const mUnassignShared = useMutation({
    mutationFn: (itemId: string) => api.unassignTodoItem(h, itemId),
    onSuccess: invalidateShared,
    onError,
  });
  // Reviewer actions on an in-review shared task, mirroring the List view: Verify
  // → completed (the server sets done=true); Send back → in_progress + done=false.
  const mVerifyShared = useMutation({
    mutationFn: (itemId: string) => api.updateTodoItem(h, itemId, { status: 'completed' }),
    onSuccess: invalidateShared,
    onError,
  });
  const mSendBackShared = useMutation({
    mutationFn: (itemId: string) =>
      api.updateTodoItem(h, itemId, { status: 'in_progress', done: false }),
    onSuccess: invalidateShared,
    onError,
  });
  const mReorderEntries = useMutation({
    mutationFn: (v: { personalSectionId: string | null; entries: TodoPersonalEntryRef[] }) =>
      api.reorderTodoPersonalEntries(h, listId, v.personalSectionId, v.entries),
    onSuccess: invalidate,
    onError,
  });
  // "+S" on a shared board card: create a real shared sub-task (trackable by
  // everyone on the list) and place it on this board as a movable card — assigned
  // to the caller (the creator). One atomic server request (create + place).
  const mCreateSubtaskOnBoard = useMutation({
    mutationFn: (v: { parentItemId: string; personalSectionId: string | null; title: string }) =>
      api.createSubtaskOnBoard(h, listId, v.parentItemId, v.personalSectionId, v.title),
    // No onError: the caller awaits this and owns the failure toast (keeps the
    // user's typed text on error).
    onSuccess: invalidateShared,
  });
  // Sync the board to the shared list's sections — one server op, three modes
  // (see syncVaultTodoPersonalBoard). Both the board and the shared surfaces can
  // shift (placements move assigned tasks), so refresh the wider detail tree.
  const mSyncBoard = useMutation({
    mutationFn: (mode: TodoPersonalBoardSyncMode) => api.syncTodoPersonalBoard(h, listId, mode),
    // No onError: handleSync (the caller) owns the sync failure toast.
    onSuccess: invalidateShared,
  });

  return {
    columns,
    sharedSections,
    // Defaults to "synced" so a still-loading board never triggers auto-sync.
    hasSynced: data?.hasSynced ?? true,
    syncBoard: (mode: TodoPersonalBoardSyncMode) => mSyncBoard.mutateAsync(mode),
    sections: (data?.sections ?? []) as TodoPersonalSectionResponse[],
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    createSection: (input: {
      name: string;
      description?: string;
      parentPersonalSectionId?: string | null;
    }) => mCreateSection.mutate(input),
    updateSection: (
      id: string,
      input: {
        name?: string;
        description?: string | null;
        parentPersonalSectionId?: string | null;
      },
    ) => mUpdateSection.mutate({ id, input }),
    deleteSection: (id: string) => mDeleteSection.mutate(id),
    reorderSections: (order: string[]) => mReorderSections.mutate(order),
    createItem: (input: {
      personalSectionId: string | null;
      title: string;
      description?: string;
    }) => mCreateItem.mutate(input),
    renamePersonalItem: (id: string, title: string) => mUpdateItem.mutate({ id, title }),
    setPersonalItemStatus: (id: string, status: TodoItemStatus) =>
      mUpdateItem.mutate({ id, status }),
    updatePersonalItem: (
      id: string,
      input: {
        title?: string;
        description?: string | null;
        personalSectionId?: string | null;
        projectId?: string | null;
      },
    ) => mUpdateItem.mutate({ id, ...input }),
    deletePersonalItem: (id: string) => mDeleteItem.mutate(id),
    restorePersonalItem: (id: string) => mRestoreItem.mutate(id),
    setSharedStatus: (itemId: string, status: TodoItemStatus) =>
      mSetSharedStatus.mutate({ itemId, status }),
    setSharedDone: (itemId: string, done: boolean) => mSetSharedDone.mutate({ itemId, done }),
    updateSharedItem: (
      itemId: string,
      input: {
        title?: string;
        description?: string | null;
        sectionId?: string | null;
        projectId?: string | null;
      },
    ) => mUpdateSharedItem.mutate({ itemId, input }),
    requestSharedReview: (itemId: string, reviewerPrincipalId: string | null) =>
      mRequestReview.mutate({ itemId, reviewerPrincipalId }),
    verifyShared: (itemId: string) => mVerifyShared.mutate(itemId),
    sendBackShared: (itemId: string) => mSendBackShared.mutate(itemId),
    assignShared: (itemId: string, assigneePrincipalId: string) =>
      mAssignShared.mutate({ itemId, assigneePrincipalId }),
    unassignShared: (itemId: string) => mUnassignShared.mutate(itemId),
    // Promise-returning twins for bulk actions (the board's runBulk uses
    // Promise.allSettled). Single actions keep the fire-and-forget mutate() forms
    // above; both share the same mutations (so onError still fires), but these let
    // the bulk path report partial failures instead of a blanket success toast.
    setSharedStatusAsync: (itemId: string, status: TodoItemStatus) =>
      mSetSharedStatus.mutateAsync({ itemId, status }),
    setPersonalItemStatusAsync: (id: string, status: TodoItemStatus) =>
      mUpdateItem.mutateAsync({ id, status }),
    updateSharedItemAsync: (
      itemId: string,
      input: {
        title?: string;
        description?: string | null;
        sectionId?: string | null;
        projectId?: string | null;
      },
    ) => mUpdateSharedItem.mutateAsync({ itemId, input }),
    updatePersonalItemAsync: (
      id: string,
      input: {
        title?: string;
        description?: string | null;
        personalSectionId?: string | null;
        projectId?: string | null;
      },
    ) => mUpdateItem.mutateAsync({ id, ...input }),
    requestSharedReviewAsync: (itemId: string, reviewerPrincipalId: string | null) =>
      mRequestReview.mutateAsync({ itemId, reviewerPrincipalId }),
    assignSharedAsync: (itemId: string, assigneePrincipalId: string) =>
      mAssignShared.mutateAsync({ itemId, assigneePrincipalId }),
    unassignSharedAsync: (itemId: string) => mUnassignShared.mutateAsync(itemId),
    deletePersonalItemAsync: (id: string) => mDeleteItem.mutateAsync(id),
    reorderEntriesAsync: (personalSectionId: string | null, entries: TodoPersonalEntryRef[]) =>
      mReorderEntries.mutateAsync({ personalSectionId, entries }),
    /** "+S": create a shared sub-task under `parentItemId` and place it here.
     *  Returns the mutation promise so the caller can await success / catch a
     *  failure (and keep the user's typed text). */
    createSubtaskOnBoard: (parentItemId: string, personalSectionId: string | null, title: string) =>
      mCreateSubtaskOnBoard.mutateAsync({ parentItemId, personalSectionId, title }),
    reorderEntries: (personalSectionId: string | null, entries: TodoPersonalEntryRef[]) =>
      mReorderEntries.mutate({ personalSectionId, entries }),
    /** Optimistically set the board while a drag persists, to avoid a flash. */
    setBoardOptimistic: (updater: (prev: typeof data) => typeof data) =>
      queryClient.setQueryData(key, (prev: typeof data) => (prev ? updater(prev) : prev)),
    /** Run `fn` with per-request error toasts suppressed, so a bulk action can
     *  report a single aggregate result instead of one toast per failed task.
     *  Depth-counted so overlapping bulk actions each keep suppression until they
     *  individually finish. */
    withBulkErrorSuppressed: async <T>(fn: () => Promise<T>): Promise<T> => {
      errorSuppressionDepthRef.current++;
      try {
        return await fn();
      } finally {
        errorSuppressionDepthRef.current--;
      }
    },
  };
}
